import { useState } from 'react';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { axiosLib } from '@/lib/axios';
import { useAuthContext } from '@/hooks/use-auth';
import { isBrowser } from '@/utils/util_helpers/ssr';
import { trackFbEvent } from '@/common/utils/facebook_pixel_utils';

export const NEWSLETTER_SOURCE = {
  COMING_SOON_MODAL: 'coming_soon_modal',
  COMING_SOON_DIRECT: 'coming_soon_direct',
  HOUSE_DETAILS_MODAL: 'house_details_modal',
  HOUSE_DETAILS_DIRECT: 'house_details_direct',
} as const;

interface NewsletterSubscriptionData {
  email: string;
  firstName?: string;
  lastName?: string;
  cityId?: number;
  houseId?: number;
  language?: string;
  phone?: string;
  source?: string;
}

interface UseNewsletterSubscriptionReturn {
  subscribe: (data: NewsletterSubscriptionData) => Promise<void>;
  subscribeNewsletter: (email: string, source?: string) => Promise<string | undefined>;
  unsubscribe: (data: Pick<NewsletterSubscriptionData, 'email' | 'cityId' | 'houseId'>) => Promise<void>;
  isLoading: boolean;
}

export const useNewsletterSubscription = (): UseNewsletterSubscriptionReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuthContext()!;
  const pageUrl = isBrowser() ? window.location.href : undefined;

  const subscribe = async (data: NewsletterSubscriptionData): Promise<void> => {
    if (!data.email) {
      toast.error('Email is required');
      return;
    }

    setIsLoading(true);
    const eventId = uuidv4();

    try {
      const response = await axiosLib.post('/api/newsletter/subscribe_listing', {
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        city_id: data.cityId,
        house_id: data.houseId,
        is_user_login: !!user,
        language: data.language,
        phone: data.phone,
        event_id: eventId,
        source: data.source,
        page_url: pageUrl,
      });

      if (response.status === 200) {
        // Same eventId the server sent to CAPI, so Meta dedupes the pair. Logged-in users already
        // carry their user data from the pixel init.
        trackFbEvent(
          'Lead',
          eventId,
          { source: data.source },
          user
            ? undefined
            : { em: data.email, fn: data.firstName, ln: data.lastName, ph: data.phone }
        );
        toast.success('Successfully subscribed to newsletter');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.error || 'Failed to subscribe to newsletter';
        toast.error(errorMessage);
      } else {
        toast.error('An unexpected error occurred');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const subscribeNewsletter = async (email: string, source?: string): Promise<string | undefined> => {
    if (!email) {
      toast.error('Email is required');
      return;
    }

    setIsLoading(true);
    const eventId = uuidv4();

    try {
      const response = await axiosLib.post('/api/newsletter/subscribe', {
        email: email,
        event_id: eventId,
        source,
        page_url: pageUrl,
      });

      if (response.status === 200) {
        toast.success('Successfully subscribed to newsletter');
        return eventId;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.error || 'Failed to subscribe to newsletter';
        toast.error(errorMessage);
      } else {
        toast.error('An unexpected error occurred');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async (data: Pick<NewsletterSubscriptionData, 'email' | 'cityId' | 'houseId'>) => {
    setIsLoading(true);

    try {
      const response = await axiosLib.post(
        '/api/newsletter/unsubscribe',
        {
          email: data.email,
          city_id: data.cityId,
          house_id: data.houseId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (response.status === 200) {
        toast.success('Successfully unsubscribed from newsletter');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.error || 'Failed to unsubscribe from newsletter';
        toast.error(errorMessage);
      } else {
        toast.error('An unexpected error occurred');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    subscribe,
    subscribeNewsletter,
    unsubscribe,
    isLoading,
  };
};
